import React, { useRef, useEffect, type FC } from 'react';
import { Provider } from 'react-redux';
import { type Store } from 'redux';
import isEqual from 'lodash/isEqual';
import { configureStore } from '../../store';
import { type WidgetInternalProps } from '../../types/WidgetProps';
import { enrichSentryWithData, setSentryTag } from '../../utils/sentry';
import { inIframe } from '../../utils/inIframe';
import { useConstructor } from '../../hooks/useConstructor';
import { BiLoggerProvider } from '../Provider/BiLogger/BiLoggerProvider';
import { FedopsProvider } from '../Provider/Fedops/FedopsProvider';
import { ExperimentsProvider } from '../Provider/Experiments/ExperimentsProvider';
import { updateTopProps } from '../../actions/payment/updateTopProps';
import { googlePayButtonInitialState, googlePayOptionsInitialState } from '../../reducers/googlePay';
import { validateWidgetProps } from './validate';
import { mergePropsWithDefaults } from './props';
import { WidgetController } from './controller';
import { areComparablePropsChanged, extractTopProps } from './utils/areComparablePropsChanged';
import '../../styles/vars.css';

export const WidgetRoot: FC<WidgetInternalProps> = (initProps) => {
  const props = mergePropsWithDefaults(initProps);
  const storeRef = useRef<Store>();
  const prevPropsRef = useRef<WidgetInternalProps>();

  useConstructor(() => {
    enrichSentryWithData({
      href: window?.location?.href,
      locale: props.locale,
      meta: props.meta,
      release: props?.widgetSettings?.widgetVersion,
    });
    setSentryTag({ key: 'sessionId', value: props.sessionId });

    validateWidgetProps(props);

    const topProps = extractTopProps(props);

    storeRef.current = configureStore({
      topProps,
      appState: {
        highestSupportedApplePayVersion: null,
        applePayCanMakePayments: false,
        isInIframe: inIframe(),
        widgetRootWrapperDh: props.widgetRootWrapperDh,
        payPalMethodsEligibility: {},
        experiments: props.preloadingData.experiments.all(),
        sessionId: props.sessionId,
      },
      googlePay: {
        options: googlePayOptionsInitialState,
        button: googlePayButtonInitialState,
        paymentData: {
          shippingMethods: topProps.shippingMethods,
        },
      },
    });
  });

  useEffect(() => {
    const prevProps = prevPropsRef.current;

    if (prevProps) {
      const topProps = extractTopProps(props);

      if (!isEqual(prevProps, props)) {
        validateWidgetProps(props);
      }

      if (areComparablePropsChanged(prevProps, props) || props.onClick !== prevProps.onClick) {
        storeRef.current?.dispatch(updateTopProps(topProps));
      }
    }

    prevPropsRef.current = props;
  }, [props]);

  if (validateWidgetProps(props, false).isError) {
    return null;
  }

  return (
    <Provider store={storeRef.current}>
      <ExperimentsProvider experiments={props.preloadingData.experiments}>
        <BiLoggerProvider
          biLoggerFactory={props.biLoggerFactory}
          meta={props.meta}
          sessionId={props.sessionId}
          requestShipping={props.requestShipping}
          purchaseFlowId={props.biData?.purchaseFlowId}
          catalogAppId={props.biData?.catalogAppId}
        >
          <FedopsProvider fedopsLogger={props.fedopsLogger}>
            <WidgetController {...props} />
          </FedopsProvider>
        </BiLoggerProvider>
      </ExperimentsProvider>
    </Provider>
  );
};
